/**
 * Common validation functions for form fields
 */

/**
 * Validate code field
 * Rules: Only letters and numbers, no special characters, max 10 characters
 * @param {string} value - The code value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateCode = (value) => {
  if (!value) return true; // Allow empty for optional fields
  const codeRegex = /^[A-Za-z0-9]+$/;
  return codeRegex.test(value) && value.length <= 10;
};

/**
 * Validate name field
 * Rules: Maximum 100 characters
 * @param {string} value - The name value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateName = (value) => {
  if (!value) return true; // Allow empty for optional fields
  return value.length <= 100;
};

/**
 * Validate description field
 * Rules: Maximum 255 characters
 * @param {string} value - The description value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateDescription = (value) => {
  if (!value) return true; // Allow empty for optional fields
  return value.length <= 255;
};

/**
 * Get validation error message for code field
 * @param {string} value - The code value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getCodeError = (value, t) => {
  if (!value) return '';
  const codeRegex = /^[A-Za-z0-9]+$/;
  if (!codeRegex.test(value)) {
    return t ? t('validation.codeOnlyAlphanumeric') : 'Mã chỉ được chứa chữ hoặc số';
  }
  if (value.length > 10) {
    return t ? t('validation.codeMaxLength', { max: 10 }) : 'Mã không được vượt quá 10 ký tự';
  }
  return '';
};

/**
 * Get validation error message for name field
 * @param {string} value - The name value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getNameError = (value, t) => {
  if (!value) return '';
  
  // Check for HTML tags first
  if (containsHtmlTags(value)) {
    return t ? t('validation.noHtmlTags') : 'Không được chứa thẻ HTML hoặc script';
  }
  
  // Check for allowed characters (letters, numbers, Vietnamese characters, spaces, dots, underscores, hyphens)
  const allowedPattern = /^[A-Za-z0-9À-ỹ\s._-]*$/;
  if (!allowedPattern.test(value)) {
    return t
      ? t('validation.nameOnlyAlphanumeric')
      : 'Tên chỉ được chứa chữ cái, số, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-)';
  }
  
  // Check max length
  if (!validateName(value)) {
    return t ? t('validation.nameMaxLength', { max: 100 }) : 'Tên không được vượt quá 100 ký tự';
  }
  
  return '';
};

/**
 * Get validation error message for description field
 * @param {string} value - The description value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getDescriptionError = (value, t) => {
  if (!value) return '';
  if (!validateDescription(value)) {
    return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
  }
  return '';
};

/**
 * Check if string contains HTML tags or script tags
 * @param {string} value - The value to check
 * @returns {boolean} - True if contains HTML/script tags
 */
export const containsHtmlTags = (value) => {
  if (!value) return false;
  // Check for <script>, <iframe>, <object>, <embed> and other HTML tags
  const htmlTagPattern = /<\/?[a-z][\s\S]*>/i;
  return htmlTagPattern.test(value);
};

/**
 * Sanitize input by removing HTML tags
 * @param {string} value - The value to sanitize
 * @returns {string} - Sanitized value without HTML tags
 */
export const sanitizeInput = (value) => {
  if (!value) return value;
  // Remove all HTML tags
  return value.replace(/<\/?[^>]+(>|$)/g, '');
};

/**
 * Validate that input does not contain HTML/script tags
 * @param {string} value - The value to validate
 * @returns {boolean} - True if valid (no HTML tags), false otherwise
 */
export const validateNoHtmlTags = (value) => {
  if (!value) return true;
  return !containsHtmlTags(value);
};

/**
 * Get validation error message for HTML tags
 * @param {string} value - The value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getHtmlTagsError = (value, t) => {
  if (!value) return '';
  if (containsHtmlTags(value)) {
    return t ? t('validation.noHtmlTags') : 'Không được chứa thẻ HTML hoặc script';
  }
  return '';
};

/**
 * Validate model/serial number field
 * Rules: Only letters and numbers (no special characters, no spaces)
 * @param {string} value - The value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateModelSerialNumber = (value) => {
  if (!value) return true; // Allow empty for optional fields
  // Allow: letters and numbers only (no spaces, no special characters)
  const allowedPattern = /^[a-zA-Z0-9]*$/;
  return allowedPattern.test(value);
};

/**
 * Get validation error message for model/serial number field
 * @param {string} value - The value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getModelSerialNumberError = (value, t) => {
  if (!value) return '';
  
  // Check for HTML tags first
  if (containsHtmlTags(value)) {
    return t ? t('validation.noHtmlTags') : 'Không được chứa thẻ HTML hoặc script';
  }
  
  // Check for allowed characters (only letters and numbers, no spaces, no special characters)
  const allowedPattern = /^[a-zA-Z0-9]*$/;
  if (!allowedPattern.test(value)) {
    return t ? t('validation.modelSerialNumberInvalidCharacters') : 'Chỉ được chứa chữ hoặc số';
  }
  
  return '';
};

/**
 * Validate license code field
 * Rules: Maximum 50 characters
 * @param {string} value - The license code value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateLicenseCode = (value) => {
  if (!value) return true; // Allow empty for optional fields
  return value.length <= 50;
};

/**
 * Get validation error message for license code field
 * @param {string} value - The license code value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getLicenseCodeError = (value, t) => {
  if (!value) return '';
  if (value.length > 50) {
    return t ? t('validation.licenseCodeMaxLength', { max: 50 }) : 'Mã giấy phép không được vượt quá 50 ký tự';
  }
  return '';
};

/**
 * Validate license key field
 * Rules: Maximum 64 characters
 * @param {string} value - The license key value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateLicenseKey = (value) => {
  if (!value) return true; // Allow empty for optional fields
  return value.length <= 64;
};

/**
 * Get validation error message for license key field
 * @param {string} value - The license key value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getLicenseKeyError = (value, t) => {
  if (!value) return '';
  if (value.length > 64) {
    return t ? t('validation.licenseKeyMaxLength', { max: 64 }) : 'Khóa giấy phép không được vượt quá 64 ký tự';
  }
  return '';
};

/**
 * Format phone number from local format (0xxxxxxxxx) to international format (+84xxxxxxxxx)
 * @param {string} phone - Phone number in local format (0xxxxxxxxx)
 * @returns {string} - Phone number in international format (+84xxxxxxxxx)
 */
export const formatPhoneToInternational = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters
  const digits = phone.replace(/\D/g, '');

  // If starts with 0, replace with +84
  if (digits.startsWith('0')) {
    return '+84' + digits.substring(1);
  }

  // If starts with 84, add +
  if (digits.startsWith('84')) {
    return '+' + digits;
  }

  // If already starts with +84, return as is
  if (phone.startsWith('+84')) {
    return phone;
  }

  // Otherwise assume it's a local number without leading 0, add +84
  return '+84' + digits;
};

/**
 * Format phone number from international format (+84xxxxxxxxx) to local format (0xxxxxxxxx)
 * @param {string} phone - Phone number in international format (+84xxxxxxxxx)
 * @returns {string} - Phone number in local format (0xxxxxxxxx)
 */
export const formatPhoneToLocal = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // If starts with +84, replace with 0
  if (cleaned.startsWith('+84')) {
    return '0' + cleaned.substring(3);
  }

  // If starts with 84, replace with 0
  if (cleaned.startsWith('84')) {
    return '0' + cleaned.substring(2);
  }

  // If already starts with 0, return as is
  if (cleaned.startsWith('0')) {
    return cleaned;
  }

  // Otherwise assume it needs a leading 0
  return '0' + cleaned;
};

/**
 * Validate phone number format
 * Accepts: 0xxxxxxxxx (9-11 digits after 0) or +84xxxxxxxxx (9-11 digits after +84)
 * @param {string} phone - Phone number to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePhoneNumber = (phone) => {
  if (!phone) return true; // Allow empty for optional fields

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Check if it's in local format (0xxxxxxxxx)
  const localPattern = /^0\d{9,11}$/;
  if (localPattern.test(cleaned)) {
    return true;
  }

  // Check if it's in international format (+84xxxxxxxxx)
  const internationalPattern = /^\+84\d{9,11}$/;
  if (internationalPattern.test(cleaned)) {
    return true;
  }

  return false;
};

/**
 * Get validation error message for phone number
 * @param {string} value - The phone value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getPhoneError = (value, t) => {
  if (!value) return '';

  if (!validatePhoneNumber(value)) {
    return t ? t('accounts.validation.phoneInvalidFormat') : 'Số điện thoại không hợp lệ';
  }

  return '';
};

/**
 * Format phone for display with proper spacing
 * Example: +84 xxx xxx xxx or 0xxx xxx xxx
 * @param {string} phone - Phone number
 * @returns {string} - Formatted phone number with spacing
 */
export const formatPhoneDisplay = (phone) => {
  if (!phone) return '';

  // Remove all non-digit characters except +
  const cleaned = phone.replace(/[^\d+]/g, '');

  // Format international: +84 xxx xxx xxx
  if (cleaned.startsWith('+84')) {
    const digits = cleaned.substring(3);
    if (digits.length <= 3) return `+84 ${digits}`;
    if (digits.length <= 6) return `+84 ${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `+84 ${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  // Format local: 0xxx xxx xxx
  if (cleaned.startsWith('0')) {
    const digits = cleaned.substring(1);
    if (digits.length <= 3) return `0${digits}`;
    if (digits.length <= 6) return `0${digits.slice(0, 3)} ${digits.slice(3)}`;
    return `0${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }

  return phone;
};

/**
 * Mask phone number for list display
 * Keeps first N characters (default 3) and replaces the rest with x
 * @param {string} phone
 * @param {number} visibleChars
 * @returns {string}
 */
export const maskPhoneDisplay = (phone, visibleChars = 3) => {
  const formatted = formatPhoneDisplay(phone);
  if (!formatted) return '';
  const compact = formatted.replace(/\s+/g, '');
  if (compact.length <= visibleChars) {
    return compact;
  }
  const masked = 'x'.repeat(compact.length - visibleChars);
  return `${compact.slice(0, visibleChars)}${masked}`;
};

/**
 * Validate role name field
 * Rules: Only letters, numbers, spaces, underscores, and hyphens; max 100 characters
 * @param {string} value - The role name value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateRoleName = (value) => {
  if (!value) return true; // Allow empty for optional fields

  // Check max length
  if (value.length > 100) return false;

  // Check for allowed characters: letters, numbers, spaces, underscore, hyphen
  const roleNamePattern = /^[A-Za-z0-9 _-]+$/;
  return roleNamePattern.test(value);
};

/**
 * Get validation error message for role name field
 * @param {string} value - The role name value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getRoleNameError = (value, t) => {
  if (!value) return '';

  if (value.length > 100) {
    return t ? t('validation.roleNameMaxLength', { max: 100 }) : 'Tên vai trò không được vượt quá 100 ký tự';
  }

  const roleNamePattern = /^[A-Za-z0-9 _-]+$/;
  if (!roleNamePattern.test(value)) {
    return t ? t('validation.roleNameInvalidCharacters') : 'Tên vai trò chỉ được chứa chữ cái, số, khoảng trắng, gạch dưới (_) và gạch ngang (-)';
  }

  return '';
};

/**
 * Check if a year is a leap year
 * @param {number} year - The year to check
 * @returns {boolean} - True if leap year, false otherwise
 */
export const isLeapYear = (year) => {
  return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
};

/**
 * Get number of days in a month
 * @param {number} month - Month (1-12)
 * @param {number} year - Year
 * @returns {number} - Number of days in the month
 */
export const getDaysInMonth = (month, year) => {
  const daysInMonth = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];
  if (month === 2 && isLeapYear(year)) {
    return 29;
  }
  return daysInMonth[month - 1];
};

/**
 * Validate that a date exists (e.g., not 31/02/2024, 30/02/2025)
 * @param {number} day - Day (1-31)
 * @param {number} month - Month (1-12)
 * @param {number} year - Year
 * @returns {boolean} - True if date exists, false otherwise
 */
export const isValidDate = (day, month, year) => {
  // Check basic ranges
  if (month < 1 || month > 12) return false;
  if (day < 1) return false;
  if (year < 1) return false;

  // Check day is within month's range
  const maxDay = getDaysInMonth(month, year);
  return day <= maxDay;
};

/**
 * Parse date string in dd/mm/yyyy format
 * @param {string} dateString - Date in dd/mm/yyyy format
 * @returns {object|null} - {day, month, year} or null if invalid format
 */
export const parseDateString = (dateString) => {
  if (!dateString) return null;

  const ddmmyyyyPattern = /^(\d{2})\/(\d{2})\/(\d{4})$/;
  const match = dateString.match(ddmmyyyyPattern);

  if (!match) return null;

  const day = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const year = parseInt(match[3], 10);

  return { day, month, year };
};

/**
 * Parse date string in yyyy-mm-dd format (ISO)
 * @param {string} dateString - Date in yyyy-mm-dd format
 * @returns {object|null} - {day, month, year} or null if invalid format
 */
export const parseISODateString = (dateString) => {
  if (!dateString) return null;

  const yyyymmddPattern = /^(\d{4})-(\d{2})-(\d{2})$/;
  const match = dateString.match(yyyymmddPattern);

  if (!match) return null;

  const year = parseInt(match[1], 10);
  const month = parseInt(match[2], 10);
  const day = parseInt(match[3], 10);

  return { day, month, year };
};

/**
 * Validate date in yyyy-mm-dd format (ISO)
 * @param {string} dateString - Date in yyyy-mm-dd format
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateDateISO = (dateString) => {
  if (!dateString) return true; // Allow empty for optional fields

  const parsed = parseISODateString(dateString);
  if (!parsed) return false;

  return isValidDate(parsed.day, parsed.month, parsed.year);
};

/**
 * Validate date in dd/mm/yyyy format
 * @param {string} dateString - Date in dd/mm/yyyy format
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateDateDDMMYYYY = (dateString) => {
  if (!dateString) return true; // Allow empty for optional fields

  const parsed = parseDateString(dateString);
  if (!parsed) return false;

  return isValidDate(parsed.day, parsed.month, parsed.year);
};

/**
 * Validate that year is within acceptable range (2000 <= year <= current year)
 * @param {string} dateString - Date in yyyy-mm-dd or dd/mm/yyyy format
 * @returns {boolean} - True if year is valid, false otherwise
 */
export const validateDateYear = (dateString) => {
  if (!dateString) return true;

  // Try ISO format first
  let parsed = parseISODateString(dateString);

  // If not ISO, try dd/mm/yyyy
  if (!parsed) {
    parsed = parseDateString(dateString);
  }

  if (!parsed) return false;

  const currentYear = new Date().getFullYear();
  return parsed.year >= 2000 && parsed.year <= currentYear;
};

/**
 * Validate that date is not in the future
 * @param {string} dateString - Date in yyyy-mm-dd format
 * @returns {boolean} - True if date is today or in the past, false if in future
 */
export const validateDateNotFuture = (dateString) => {
  if (!dateString) return true;

  const parsed = parseISODateString(dateString);
  if (!parsed) return false;

  const inputDate = new Date(parsed.year, parsed.month - 1, parsed.day);
  const today = new Date();
  today.setHours(23, 59, 59, 999); // End of today

  return inputDate <= today;
};

/**
 * Validate date range (start date must be <= end date)
 * @param {string} startDate - Start date in yyyy-mm-dd format
 * @param {string} endDate - End date in yyyy-mm-dd format
 * @returns {boolean} - True if range is valid, false otherwise
 */
export const validateDateRange = (startDate, endDate) => {
  // If either date is empty, consider valid (will be handled by required field validation)
  if (!startDate || !endDate) return true;

  const startParsed = parseISODateString(startDate);
  const endParsed = parseISODateString(endDate);

  if (!startParsed || !endParsed) return false;

  const start = new Date(startParsed.year, startParsed.month - 1, startParsed.day);
  const end = new Date(endParsed.year, endParsed.month - 1, endParsed.day);

  return start <= end;
};

/**
 * Check if both start and end dates are provided
 * @param {string} startDate - Start date
 * @param {string} endDate - End date
 * @returns {boolean} - True if both provided or both empty, false if only one provided
 */
export const validateDateRangeComplete = (startDate, endDate) => {
  const hasStart = startDate && startDate.trim() !== '';
  const hasEnd = endDate && endDate.trim() !== '';

  // Both empty is valid
  if (!hasStart && !hasEnd) return true;

  // Both filled is valid
  if (hasStart && hasEnd) return true;

  // Only one filled is invalid
  return false;
};

/**
 * Convert date from dd/mm/yyyy to yyyy-mm-dd format
 * @param {string} ddmmyyyy - Date in dd/mm/yyyy format
 * @returns {string} - Date in yyyy-mm-dd format or empty string if invalid
 */
export const convertDateToISO = (ddmmyyyy) => {
  if (!ddmmyyyy) return '';

  const parsed = parseDateString(ddmmyyyy);
  if (!parsed) return '';

  const { day, month, year } = parsed;
  const dayStr = day.toString().padStart(2, '0');
  const monthStr = month.toString().padStart(2, '0');

  return `${year}-${monthStr}-${dayStr}`;
};

/**
 * Convert date from yyyy-mm-dd to dd/mm/yyyy format
 * @param {string} yyyymmdd - Date in yyyy-mm-dd format
 * @returns {string} - Date in dd/mm/yyyy format or empty string if invalid
 */
export const convertDateToDDMMYYYY = (yyyymmdd) => {
  if (!yyyymmdd) return '';

  const parsed = parseISODateString(yyyymmdd);
  if (!parsed) return '';

  const { day, month, year } = parsed;
  const dayStr = day.toString().padStart(2, '0');
  const monthStr = month.toString().padStart(2, '0');

  return `${dayStr}/${monthStr}/${year}`;
};

/**
 * Get comprehensive validation error for a date field
 * @param {string} value - Date value in yyyy-mm-dd format
 * @param {function} t - Translation function
 * @param {object} options - Additional validation options
 * @returns {string} - Error message or empty string
 */
export const getDateError = (value, t, options = {}) => {
  if (!value) {
    if (options.required) {
      return t ? t('validation.dateRequired') : 'Vui lòng nhập ngày';
    }
    return '';
  }

  // If simpleMessage option is true, return a generic invalid message for any invalid date
  if (options.simpleMessage) {
    // Check if date is invalid in any way
    const isValidFormat = validateDateISO(value);
    const isValidYear = validateDateYear(value);
    const isValidNotFuture = options.noFuture ? validateDateNotFuture(value) : true;
    
    if (!isValidFormat || !isValidYear || !isValidNotFuture) {
      return t ? t('validation.dateInvalid') : 'Ngày không hợp lệ';
    }
    return '';
  }

  // Check format
  if (!validateDateISO(value)) {
    const parsed = parseISODateString(value);
    if (!parsed) {
      return t ? t('validation.dateInvalidFormat') : 'Định dạng ngày không hợp lệ. Vui lòng nhập theo định dạng dd/mm/yyyy';
    }
    return t ? t('validation.dateInvalidDate') : 'Ngày không tồn tại. Vui lòng kiểm tra lại';
  }

  // Check year range
  if (!validateDateYear(value)) {
    return t ? t('validation.dateInvalidYear') : 'Năm không hợp lệ. Chỉ chấp nhận từ năm 2000 đến hiện tại';
  }

  // Check not in future
  if (options.noFuture && !validateDateNotFuture(value)) {
    return t ? t('validation.dateNoFuture') : 'Không thể chọn ngày trong tương lai';
  }

  return '';
};

/**
 * Get validation error for date range
 * @param {string} startDate - Start date in yyyy-mm-dd format
 * @param {string} endDate - End date in yyyy-mm-dd format
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getDateRangeError = (startDate, endDate, t) => {
  // Check if both dates are provided
  if (!validateDateRangeComplete(startDate, endDate)) {
    return t ? t('validation.dateRangeIncomplete') : 'Vui lòng nhập đủ cả ngày bắt đầu và ngày kết thúc';
  }

  // If both empty, no error
  if (!startDate && !endDate) {
    return '';
  }

  // Check individual date validity
  const startError = getDateError(startDate, t);
  if (startError) return startError;

  const endError = getDateError(endDate, t);
  if (endError) return endError;

  // Check range order
  if (!validateDateRange(startDate, endDate)) {
    return t ? t('validation.dateRangeInvalid') : 'Khoảng thời gian không hợp lệ. Ngày bắt đầu phải trước hoặc bằng ngày kết thúc';
  }

  return '';
};

/**
 * Validate non-negative integer within a range (allows 0 and positive integers)
 * @param {string|number} value - The value to validate
 * @param {number} min - Minimum value (inclusive)
 * @param {number} max - Maximum value (inclusive)
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePositiveIntegerRange = (value, min, max) => {
  if (!value && value !== 0) return false;

  // Convert to number
  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  // Check if it's a valid number
  if (isNaN(numValue)) return false;

  // Check if it's an integer
  if (!Number.isInteger(numValue)) return false;

  // Check if it's non-negative (>= 0)
  if (numValue < 0) return false;

  // Check range
  return numValue >= min && numValue <= max;
};

/**
 * Get validation error message for non-negative integer range
 * @param {string|number} value - The value
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getPositiveIntegerRangeError = (value, min, max, t) => {
  if (!value && value !== 0) {
    return t ? t('settings.validation.required') : 'Vui lòng nhập giá trị';
  }

  const numValue = typeof value === 'string' ? parseFloat(value) : value;

  if (isNaN(numValue)) {
    return t ? t('settings.validation.invalidNumber') : 'Giá trị phải là số';
  }

  if (!Number.isInteger(numValue)) {
    return t ? t('settings.validation.mustBeInteger') : 'Giá trị phải là số nguyên';
  }

  if (numValue < 0) {
    return t ? t('settings.validation.mustBeNonNegative') : 'Giá trị phải là số không âm (từ 0 trở lên)';
  }

  if (numValue < min || numValue > max) {
    return t
      ? t('settings.validation.invalidRange', { min, max })
      : `Giá trị phải trong khoảng ${min} - ${max}`;
  }

  return '';
};

/**
 * Validate file extension format
 * Rules: Must start with dot, followed by lowercase letters, numbers, underscore, or plus sign
 * Only allows: .a-z, 0-9, _, + (no other special characters)
 * @param {string} value - The file extension to validate (e.g., ".pdf", ".docx")
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateFileExtension = (value) => {
  if (!value || typeof value !== 'string') return false;
  
  const trimmedValue = value.trim();
  if (!trimmedValue) return false;
  
  // Must start with dot, followed by one or more alphanumeric, underscore, or plus
  // Pattern: /^\.[a-z0-9_+]+$/i (case insensitive)
  // This ensures:
  // - Starts with dot (.)
  // - Followed by at least one character from [a-z0-9_+]
  // - No other characters allowed
  const fileExtensionPattern = /^\.[a-z0-9_+]+$/i;
  
  // Additional check: ensure no special characters like !, <, >, `, @, #, etc.
  // This regex should already catch them, but let's be explicit
  if (!fileExtensionPattern.test(trimmedValue)) {
    return false;
  }
  
  // Ensure it's not just a dot
  if (trimmedValue === '.') {
    return false;
  }
  
  return true;
};

/**
 * Get validation error message for file extension
 * @param {string} value - The file extension value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getFileExtensionError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('settings.validation.fileExtensionRequired') : 'Vui lòng nhập phần mở rộng file';
  }
  
  const trimmedValue = value.trim();
  const maxLength = SETTINGS_MAX_LENGTH.file_allowed_extensions || 10;
  
  // Check max length first
  if (trimmedValue.length > maxLength) {
    return t
      ? t('settings.validation.maxLengthExceeded', { max: maxLength })
      : `Độ dài tối đa là ${maxLength} ký tự`;
  }
  
  if (!validateFileExtension(trimmedValue)) {
    return t 
      ? t('settings.validation.invalidFileExtensionFormat') 
      : 'Định dạng phần mở rộng không hợp lệ. Chỉ cho phép: .a-z, 0-9, _, +';
  }
  
  return '';
};

/**
 * Validate email format
 * Rules: Must match standard email format: local@domain.tld
 * Pattern: ^[^\s@]+@[^\s@]+\.[^\s@]+$
 * @param {string} value - The email to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateEmail = (value) => {
  if (!value || typeof value !== 'string') return false;
  
  const trimmed = value.trim();
  if (!trimmed) return false;
  
  // Standard email pattern: local@domain.tld
  // Must have exactly one @, and at least one dot after @
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!emailPattern.test(trimmed)) {
    return false;
  }
  
  // Additional checks to prevent invalid patterns like @@@@@, #####
  // Ensure @ is not repeated multiple times
  const atCount = (trimmed.match(/@/g) || []).length;
  if (atCount !== 1) {
    return false;
  }
  
  // Ensure no consecutive special characters like @@@, ###, <<<, >>>
  if (/[@#<>]{2,}/.test(trimmed)) {
    return false;
  }
  
  // Ensure domain part (after @) has at least one dot and valid characters
  const parts = trimmed.split('@');
  if (parts.length !== 2) return false;
  
  const domain = parts[1];
  if (!domain || !domain.includes('.')) return false;
  
  // Domain should not contain consecutive special characters
  if (/[#<>!]{2,}/.test(domain)) {
    return false;
  }
  
  return true;
};

/**
 * Validate user ID format (alphanumeric, may contain underscore or hyphen, 1-50 chars)
 * Rules: Only letters, numbers, underscore, and hyphen. No special characters like @, #, <, >, !
 * @param {string} value - The user ID to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateUserId = (value) => {
  if (!value || typeof value !== 'string') return false;
  
  const trimmed = value.trim();
  if (trimmed.length < 1 || trimmed.length > 50) return false;
  
  // Alphanumeric, underscore, hyphen only
  // Must not contain special characters like @, #, <, >, !, etc.
  const userIdPattern = /^[A-Za-z0-9_-]+$/;
  
  if (!userIdPattern.test(trimmed)) {
    return false;
  }
  
  // Additional check: ensure no consecutive special characters
  // This is already covered by the pattern, but let's be explicit
  if (/[@#<>!]{2,}/.test(trimmed)) {
    return false;
  }
  
  return true;
};

/**
 * Validate notification recipient (email or user ID)
 * Rules: Must be either a valid email format or a valid user ID
 * @param {string} value - The recipient value to validate
 * @returns {boolean} - True if valid (email or user ID), false otherwise
 */
export const validateNotificationRecipient = (value) => {
  if (!value || typeof value !== 'string') return false;
  
  const trimmed = value.trim();
  if (!trimmed) return false;
  
  // Must be either a valid email OR a valid user ID
  // Check email first (more specific pattern)
  if (validateEmail(trimmed)) {
    return true;
  }
  
  // If not email, check if it's a valid user ID
  if (validateUserId(trimmed)) {
    return true;
  }
  
  // Neither email nor user ID
  return false;
};

/**
 * Get validation error message for notification recipient
 * @param {string} value - The recipient value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getNotificationRecipientError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('settings.validation.recipientRequired') : 'Vui lòng nhập email hoặc ID người dùng';
  }
  
  if (!validateNotificationRecipient(value)) {
    return t 
      ? t('settings.validation.invalidRecipientFormat') 
      : 'Định dạng không hợp lệ. Vui lòng nhập email hợp lệ (ví dụ: user@example.com) hoặc ID người dùng';
  }
  
  return '';
};

/**
 * Validate firmware version format
 * Allows alphanumeric characters, dots, underscores, hyphens, spaces, and parentheses
 * @param {string} value - The version string to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateVersion = (value) => {
  if (!value) return false;

  const versionRegex = /^[A-Za-z0-9._\-() ]+$/;
  return versionRegex.test(value);
};

/**
 * Get validation error message for version field
 * @param {string} value - The version value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getVersionError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('validation.versionRequired') : 'Vui lòng nhập phiên bản';
  }

  if (value.length > 50) {
    return t ? t('validation.versionMaxLength', { max: 50 }) : 'Phiên bản không được vượt quá 50 ký tự';
  }

  if (!validateVersion(value)) {
    return t
      ? t('validation.versionInvalidFormat')
      : 'Phiên bản chỉ được chứa chữ cái, số, dấu chấm (.), gạch dưới (_), gạch ngang (-), dấu cách và dấu ngoặc đơn ()';
  }

  return '';
};

/**
 * Validate setting text field (no special dangerous characters)
 * Rules: Only alphanumeric, spaces, dots, underscores, hyphens, colons, forward slashes
 * Blocks: < > & " ' ; ( ) { } [ ] | \ ` $ and other dangerous characters
 * @param {string} value - The text value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateSettingTextField = (value) => {
  if (!value) return true; // Allow empty for optional fields

  // Allow: letters, numbers, spaces, dots, underscores, hyphens, colons, forward slashes
  // Block: special characters that could be used for injection or scripts
  const allowedPattern = /^[A-Za-z0-9\s._\-:\/]+$/;
  return allowedPattern.test(value);
};

/**
 * Get validation error message for setting text field
 * @param {string} value - The text value
 * @param {string} key - The setting key (optional, for max length validation)
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getSettingTextFieldError = (value, key, t) => {
  if (!value) return '';

  // Check for invalid characters
  if (!validateSettingTextField(value)) {
    return t
      ? t('settings.validation.invalidCharacters')
      : 'Chỉ cho phép chữ cái, số, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-), dấu hai chấm (:), dấu gạch chéo (/)';
  }

  // Check max length if key is provided
  if (key && SETTINGS_MAX_LENGTH[key]) {
    const maxLength = SETTINGS_MAX_LENGTH[key];
    if (value.length > maxLength) {
      return t
        ? t('settings.validation.maxLengthExceeded', { max: maxLength })
        : `Độ dài tối đa là ${maxLength} ký tự`;
    }
  } else if (key && SETTINGS_MAX_LENGTH.default) {
    // Use default max length if no specific rule
    const maxLength = SETTINGS_MAX_LENGTH.default;
    if (value.length > maxLength) {
      return t
        ? t('settings.validation.maxLengthExceeded', { max: maxLength })
        : `Độ dài tối đa là ${maxLength} ký tự`;
    }
  }

  return '';
};

/**
 * Settings validation rules - defines min/max ranges for each setting key
 */
export const SETTINGS_VALIDATION_RULES = {
  // File Configuration
  file_max_size: { min: 1024, max: 2147483648 }, // 1KB to 2GB (in bytes)

  // System Logs
  log_retention_days: { min: 7, max: 3650 }, // 7 to 3650 days (10 years)

  // Backup Configuration
  backup_schedule_days: { min: 1, max: 365 }, // 1 to 365 days
  backup_retention_days: { min: 1, max: 365 }, // 1 to 365 days

  // VNeID Integration - Timeout values (1-300 seconds)
  vneid_connection_timeout: { min: 1, max: 300 }, // 1 to 300 seconds
  vneid_request_timeout: { min: 1, max: 300 }, // 1 to 300 seconds
  vneid_query_timeout: { min: 1, max: 300 }, // 1 to 300 seconds

  // Password Security
  password_min_length: { min: 4, max: 128 }, // 4 to 128 characters
};

/**
 * Settings max length rules - defines max character length for text fields
 */
export const SETTINGS_MAX_LENGTH = {
  // VNeID Integration
  vneid_system_id: 64, // System ID max 64 characters
  vneid_endpoint_url: 255, // Endpoint URL max 255 characters
  
  // File Extensions
  file_allowed_extensions: 10, // Each extension max 10 characters (e.g., ".docx" = 5 chars)
  
  // Default for other text fields
  default: 255, // Default max length for other text fields
};

/**
 * Validate Position name field
 * Rules: Letters, numbers, Vietnamese characters, spaces, dots, underscores, hyphens (1-100 characters)
 * @param {string} value - The name value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePositionName = (value) => {
  if (!value) return false;
  const positionNameRegex = /^[A-Za-z0-9À-ỹ\s._-]{1,100}$/;
  return positionNameRegex.test(value);
};

/**
 * Get validation error message for Position name field
 * @param {string} value - The name value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getPositionNameError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('validation.nameRequired') : 'Vui lòng nhập tên';
  }

  if (!validatePositionName(value)) {
    if (value.length > 100) {
      return t ? t('validation.nameMaxLength', { max: 100 }) : 'Tên không được vượt quá 100 ký tự';
    }
    return t
      ? t('validation.positionNameInvalidFormat')
      : 'Tên chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validate Position description field
 * Rules: Letters, numbers, Vietnamese characters, spaces, dots, commas, underscores, hyphens (0-255 characters)
 * @param {string} value - The description value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validatePositionDescription = (value) => {
  if (!value) return true; // Description is optional
  const positionDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$/;
  return positionDescriptionRegex.test(value);
};

/**
 * Get validation error message for Position description field
 * @param {string} value - The description value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getPositionDescriptionError = (value, t) => {
  if (!value) return '';

  if (!validatePositionDescription(value)) {
    if (value.length > 255) {
      return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
    }
    return t
      ? t('validation.positionDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), dấu phẩy (,), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validate VisitType name field
 * Rules: ^[A-Za-z0-9À-ỹ\s._-]{1,100}$ (letters, numbers, Vietnamese, spaces, dots, underscores, hyphens, 1-100 chars)
 * @param {string} value - The name value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateVisitTypeName = (value) => {
  if (!value) return false; // Required field
  const visitTypeNameRegex = /^[A-Za-z0-9À-ỹ\s._-]{1,100}$/;
  return visitTypeNameRegex.test(value);
};

/**
 * Get validation error message for VisitType name field
 * @param {string} value - The name value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getVisitTypeNameError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('visitTypes.addDialog.nameRequired') : 'Tên là bắt buộc';
  }

  if (!validateVisitTypeName(value)) {
    if (value.length > 100) {
      return t ? t('validation.nameMaxLength', { max: 100 }) : 'Tên không được vượt quá 100 ký tự';
    }
    return t
      ? t('validation.visitTypeNameInvalidFormat')
      : 'Tên chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validate VisitType description field
 * Rules: ^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$ (letters, numbers, Vietnamese, spaces, dots, commas, underscores, hyphens, 0-255 chars, optional)
 * @param {string} value - The description value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateVisitTypeDescription = (value) => {
  if (!value) return true; // Optional field
  const visitTypeDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$/;
  return visitTypeDescriptionRegex.test(value);
};

/**
 * Get validation error message for VisitType description field
 * @param {string} value - The description value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getVisitTypeDescriptionError = (value, t) => {
  if (!value) return ''; // Optional field

  if (!validateVisitTypeDescription(value)) {
    if (value.length > 255) {
      return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
    }
    return t
      ? t('validation.visitTypeDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), dấu phẩy (,), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validate IdentityType name field
 * Rules: ^[A-Za-z0-9À-ỹ\s._-]{1,100}$ (letters, numbers, Vietnamese, spaces, dots, underscores, hyphens, 1-100 chars)
 * @param {string} value - The name value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateIdentityTypeName = (value) => {
  if (!value) return false; // Required field
  const identityTypeNameRegex = /^[A-Za-z0-9À-ỹ\s._-]{1,100}$/;
  return identityTypeNameRegex.test(value);
};

/**
 * Get validation error message for IdentityType name field
 * @param {string} value - The name value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getIdentityTypeNameError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('validation.nameRequired') : 'Vui lòng nhập tên';
  }

  if (!validateIdentityTypeName(value)) {
    if (value.length > 100) {
      return t ? t('validation.nameMaxLength', { max: 100 }) : 'Tên không được vượt quá 100 ký tự';
    }
    return t
      ? t('validation.identityTypeNameInvalidFormat')
      : 'Tên chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validate IdentityType description field
 * Rules: ^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$ (letters, numbers, Vietnamese, spaces, dots, commas, underscores, hyphens, 0-255 chars, optional)
 * @param {string} value - The description value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateIdentityTypeDescription = (value) => {
  if (!value) return true; // Optional field
  const identityTypeDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$/;
  return identityTypeDescriptionRegex.test(value);
};

/**
 * Get validation error message for IdentityType description field
 * @param {string} value - The description value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getIdentityTypeDescriptionError = (value, t) => {
  if (!value) return ''; // Optional field

  if (!validateIdentityTypeDescription(value)) {
    if (value.length > 255) {
      return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
    }
    return t
      ? t('validation.identityTypeDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), dấu phẩy (,), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

// ============================================================================
// Vehicle Type Validation
// ============================================================================

export const validateVehicleDescription = (value) => {
  if (!value) return true; // Optional field
  const vehicleDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$/;
  return vehicleDescriptionRegex.test(value);
};

export const getVehicleDescriptionError = (value, t) => {
  if (!value) return ''; // Optional field

  if (!validateVehicleDescription(value)) {
    if (value.length > 255) {
      return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
    }
    return t
      ? t('validation.vehicleDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), dấu phẩy (,), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

// ============================================================================
// Entry/Exit Reason Validation
// ============================================================================

export const validateEntryExitReasonName = (value) => {
  if (!value) return false; // Required field
  const entryExitReasonNameRegex = /^[A-Za-z0-9À-ỹ\s._-]{1,200}$/;
  return entryExitReasonNameRegex.test(value);
};

export const getEntryExitReasonNameError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('validation.nameRequired') : 'Vui lòng nhập tên';
  }

  if (!validateEntryExitReasonName(value)) {
    if (value.length > 200) {
      return t ? t('validation.nameMaxLength', { max: 200 }) : 'Tên không được vượt quá 200 ký tự';
    }
    return t
      ? t('validation.entryExitReasonNameInvalidFormat')
      : 'Tên chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

export const validateEntryExitReasonDescription = (value) => {
  if (!value) return true; // Optional field
  const entryExitReasonDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_-]{0,255}$/;
  return entryExitReasonDescriptionRegex.test(value);
};

export const getEntryExitReasonDescriptionError = (value, t) => {
  if (!value) return ''; // Optional field

  if (!validateEntryExitReasonDescription(value)) {
    if (value.length > 255) {
      return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
    }
    return t
      ? t('validation.entryExitReasonDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng, dấu chấm (.), dấu phẩy (,), gạch dưới (_), gạch ngang (-)';
  }

  return '';
};

/**
 * Validation constants
 */
export const VALIDATION_RULES = {
  CODE_PATTERN: /^[A-Za-z0-9]+$/,
  CODE_MAX_LENGTH: 32,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 255,
  ENTRY_EXIT_REASON_NAME_MAX_LENGTH: 100,
  LICENSE_CODE_MAX_LENGTH: 50,
  LICENSE_KEY_MAX_LENGTH: 64,
  VERSION_PATTERN: /^[A-Za-z0-9._\-() ]+$/,
  VERSION_MAX_LENGTH: 50,
  PHONE_LOCAL_PATTERN: /^0\d{9,11}$/,
  PHONE_INTERNATIONAL_PATTERN: /^\+84\d{9,11}$/,
  ROLE_NAME_PATTERN: /^[A-Za-z0-9 _-]+$/,
  ROLE_NAME_MAX_LENGTH: 100,
  DATE_ISO_PATTERN: /^(\d{4})-(\d{2})-(\d{2})$/,
  DATE_DDMMYYYY_PATTERN: /^(\d{2})\/(\d{2})\/(\d{4})$/,
  MIN_YEAR: 2000,
  FILE_EXTENSION_PATTERN: /^\.[a-z0-9_+]+$/i,
  EMAIL_PATTERN: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  USER_ID_PATTERN: /^[A-Za-z0-9_-]+$/,
  ROLE_DESCRIPTION_MIN_LENGTH: 10,
};

// ============================================================================
// Role Description Validation
// ============================================================================

/**
 * Validate Role description field
 * Rules: Required, minimum 10 characters, maximum 255 characters, Vietnamese characters allowed
 * @param {string} value - The description value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateRoleDescription = (value) => {
  if (!value || !value.trim()) return false; // Required field
  const trimmed = value.trim();
  if (trimmed.length < 10) return false; // Minimum 10 characters
  if (trimmed.length > 255) return false; // Maximum 255 characters
  // Allow letters, numbers, Vietnamese characters, spaces, and common punctuation
  const roleDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_\-()]+$/;
  return roleDescriptionRegex.test(trimmed);
};

/**
 * Get validation error message for Role description field
 * @param {string} value - The description value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getRoleDescriptionError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('validation.roleDescriptionRequired') : 'Vui lòng nhập mô tả vai trò';
  }

  const trimmed = value.trim();

  if (trimmed.length < 10) {
    return t ? t('validation.roleDescriptionMinLength', { min: 10 }) : 'Mô tả vai trò phải có ít nhất 10 ký tự';
  }

  if (trimmed.length > 255) {
    return t ? t('validation.descriptionMaxLength', { max: 255 }) : 'Mô tả không được vượt quá 255 ký tự';
  }

  const roleDescriptionRegex = /^[A-Za-z0-9À-ỹ\s.,_\-()]+$/;
  if (!roleDescriptionRegex.test(trimmed)) {
    return t
      ? t('validation.roleDescriptionInvalidFormat')
      : 'Mô tả chỉ được chứa chữ cái, số, tiếng Việt, khoảng trắng và các ký tự .,_-()';
  }

  return '';
};

/**
 * Validate URL format (RFC 3986 compliant)
 * @param {string} url - The URL to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateURL = (url) => {
  if (!url || !url.trim()) return false;
  
  try {
    const urlObj = new URL(url.trim());
    // Check if protocol is http or https
    return urlObj.protocol === 'http:' || urlObj.protocol === 'https:';
  } catch {
    // If URL constructor throws, it's invalid
    return false;
  }
};

/**
 * Get validation error message for URL field
 * @param {string} url - The URL value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getURLError = (url, t) => {
  if (!url || !url.trim()) {
    return t ? t('settings.validation.urlRequired') : 'Vui lòng nhập URL';
  }
  
  if (!validateURL(url)) {
    return t ? t('settings.validation.invalidURL') : 'URL không hợp lệ. URL phải bắt đầu bằng http:// hoặc https://';
  }
  
  return '';
};

/**
 * Allowed Query Types enum values
 */
export const ALLOWED_QUERY_TYPES = ['BASIC', 'ADVANCED', 'BATCH'];

/**
 * Validate Allowed Query Types value
 * @param {string} value - The query type value to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const validateQueryType = (value) => {
  if (!value || !value.trim()) return false;
  return ALLOWED_QUERY_TYPES.includes(value.trim().toUpperCase());
};

/**
 * Get validation error message for Query Type field
 * @param {string} value - The query type value
 * @param {function} t - Translation function
 * @returns {string} - Error message or empty string
 */
export const getQueryTypeError = (value, t) => {
  if (!value || !value.trim()) {
    return t ? t('settings.validation.queryTypeRequired') : 'Vui lòng nhập loại truy vấn';
  }
  
  if (!validateQueryType(value)) {
    const typesStr = ALLOWED_QUERY_TYPES.join(', ');
    return t 
      ? t('settings.validation.invalidQueryType', { types: typesStr })
      : `Loại truy vấn không hợp lệ. Chỉ cho phép: ${typesStr}`;
  }
  
  return '';
};
